import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
} from '@fluentui/react-components';
import { AlertTriangle, CircleCheck, CircleHelp, CircleSlash, Clock, Laptop } from 'lucide-react';

import type { MigrationDevice, ReadinessSummary } from '../api/migrations';
import { checkLabel, rollupReadiness } from '../lib/readinessRollup';
import { MetricCard } from './abPrimitives';
import { DataTable, DataTableColumn } from './DataTable';
import type { StateName } from '../theme/tokens';
import { NUMERIC, TEXT } from '../theme/tokens';

// FD-494 — mockup 2's readiness tiles, and the per-check breakdown behind them.
//
// THE TILE ROW ALWAYS ADDS UP, BY CONSTRUCTION. `Total` is the SUM of the verdict
// buckets rather than a separately-sourced number, so there is no arrangement of
// the data in which the row shows five figures an operator can add and get a
// different answer from. That is the same property FD-472 built into the groups
// state tabs, for the same reason: a total a reader can falsify is worse than no
// total.
//
// `migration.deviceCount` is deliberately NOT the total. It is a stored column on
// the `Migrations` row, while `readinessSummary.byStatus` is computed from the
// same partition read that produced the grid's own rows — so the tiles agree with
// what is on screen. If the stored column has drifted, that is a condition worth
// seeing in the header rather than one worth hiding here.
//
// THE MOCKUP DRAWS FIVE TILES AND `byStatus` HAS FIVE BUCKETS PLUS A TOTAL, so
// one of them cannot have a permanent tile. `unknown` is the one that is rendered
// ONLY WHEN NON-ZERO — the FD-472 "tabs are rendered only when non-empty" rule.
// The alternative was folding `unknown` into `Not ready`, and that is exactly the
// FD-246 collapse this codebase keeps refusing: `blocked` means a check failed
// and `unknown` means the facts never arrived, so the next operator action is the
// device in one case and the agent in the other. Rendering it conditionally keeps
// both the distinction AND the arithmetic, and on the common deployment — where
// `unknown` is 0 — the row is the mockup's five tiles exactly.
//
// NO TILE CARRIES A DELTA. `MetricCard` has no delta slot by construction
// (FD-ADR-043 D4); nothing stores a previous value, so the mockup's "↑ 12%" would
// have to be invented.

export interface ReadinessTile {
  key: string;
  label: string;
  caption: string;
  value: number;
  tone?: StateName;
  /** True for the four/five verdict buckets; false for the total. */
  verdict: boolean;
}

const EMPTY_SUMMARY: ReadinessSummary['byStatus'] = {
  pass: 0,
  warn: 0,
  unknown: 0,
  blocked: 0,
  notEvaluated: 0,
};

/**
 * Pure, so the arithmetic is assertable without rendering. Returns the total tile
 * first and the verdict tiles after it, in the mockup's order.
 */
export function computeReadinessTiles(summary?: ReadinessSummary | null): ReadinessTile[] {
  const by = summary?.byStatus ?? EMPTY_SUMMARY;
  const total = by.pass + by.warn + by.unknown + by.blocked + by.notEvaluated;

  const tiles: ReadinessTile[] = [
    {
      key: 'total',
      label: 'Total devices',
      caption: 'Assigned to this campaign',
      value: total,
      verdict: false,
    },
    {
      key: 'pass',
      label: 'Ready',
      caption: 'No checks flagged',
      value: by.pass,
      tone: 'success',
      verdict: true,
    },
    {
      key: 'warn',
      label: 'Warnings',
      caption: 'Flagged, not blocking',
      value: by.warn,
      tone: 'warning',
      verdict: true,
    },
    {
      key: 'blocked',
      label: 'Not ready',
      caption: 'A blocking check failed',
      value: by.blocked,
      tone: 'critical',
      verdict: true,
    },
  ];

  // Only when it has something to say — see the header note.
  if (by.unknown > 0) {
    tiles.push({
      key: 'unknown',
      label: 'Unknown',
      caption: 'Facts did not arrive',
      value: by.unknown,
      tone: 'warning',
      verdict: true,
    });
  }

  tiles.push({
    key: 'notEvaluated',
    label: 'Not evaluated',
    caption: 'No manifest received',
    value: by.notEvaluated,
    tone: 'neutral',
    verdict: true,
  });

  return tiles;
}

const ICON: Record<string, React.ReactNode> = {
  total: <Laptop size={18} strokeWidth={1.5} />,
  pass: <CircleCheck size={18} strokeWidth={1.5} />,
  warn: <AlertTriangle size={18} strokeWidth={1.5} />,
  blocked: <CircleSlash size={18} strokeWidth={1.5} />,
  unknown: <CircleHelp size={18} strokeWidth={1.5} />,
  notEvaluated: <Clock size={18} strokeWidth={1.5} />,
};

export interface DeviceReadinessTilesProps {
  summary?: ReadinessSummary | null;
  /** The rows on screen — the per-check breakdown is computed from them. */
  devices: MigrationDevice[];
}

export default function DeviceReadinessTiles({ summary, devices }: DeviceReadinessTilesProps) {
  const tiles = computeReadinessTiles(summary);

  return (
    // A labelled region rather than a bare div: the tile labels ("Ready", "Not
    // ready") are the SAME WORDS the per-device `ReadinessBadge` renders in the
    // grid below, so a reader — assistive technology or a test — needs a way to
    // say which one it means. Naming the region is the cheapest way to give the
    // summary an identity distinct from the rows it summarises.
    <section aria-label="Readiness summary" className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
        {tiles.map((t) => (
          <MetricCard
            key={t.key}
            label={t.label}
            value={t.value.toLocaleString()}
            caption={t.caption}
            icon={ICON[t.key]}
            tone={t.tone}
          />
        ))}
      </div>

      <CheckBreakdown devices={devices} summary={summary} />
    </section>
  );
}

// The per-check table, collapsed by default.
//
// THE THIRD COLUMN IS "NOT FLAGGED", NOT "PASSED", and `readinessRollup.ts`'s own
// header says why: a device row carries only the checks that were FLAGGED, so a
// check absent from both arrays also covers one that did not run or that the
// deployment's policy does not evaluate. The mockup's "Pass Rate" is a number the
// platform cannot produce.
//
// Disclosure state is LOCAL to the Accordion rather than in the URL. The ADR-015
// rule that survived two layout changes keeps VIEW state — what is filtered, what
// is selected — in the URL so a page can be shared; whether a reference table is
// expanded is not that, and it is the same line FD-478 and FD-491 drew for dialog
// state.
function CheckBreakdown({
  devices,
  summary,
}: {
  devices: MigrationDevice[];
  summary?: ReadinessSummary | null;
}) {
  const rollup = rollupReadiness(devices, summary);

  const columns: DataTableColumn<(typeof rollup.rows)[number]>[] = [
    {
      key: 'check',
      header: 'Check',
      render: (r) => <span style={{ color: TEXT.primary }}>{checkLabel(r.check)}</span>,
    },
    {
      key: 'blocked',
      header: 'Blocked',
      numeric: true,
      align: 'right',
      render: (r) => (
        <span
          style={NUMERIC}
          // `serverCounted` is not decoration: where the server's own aggregate
          // exists it wins, because it was computed from the same partition read
          // and the grid may be a page behind. Saying which produced the figure is
          // what stops an operator reconciling two numbers by hand.
          title={r.serverCounted ? 'From the wave aggregate' : 'Counted from the rows on screen'}
        >
          {r.blocked.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'warned',
      header: 'Warned',
      numeric: true,
      align: 'right',
      render: (r) => <span style={NUMERIC}>{r.warned.toLocaleString()}</span>,
    },
    {
      key: 'notFlagged',
      header: 'Not flagged',
      numeric: true,
      align: 'right',
      render: (r) => <span style={NUMERIC}>{r.notFlagged.toLocaleString()}</span>,
    },
  ];

  return (
    <Accordion collapsible>
      <AccordionItem value="checks">
        <AccordionHeader>
          <span className="text-[13px] font-medium" style={{ color: TEXT.primary }}>
            Readiness checks
          </span>
          <span className="ml-2 text-[12px]" style={{ color: TEXT.secondary, ...NUMERIC }}>
            {rollup.evaluated.toLocaleString()} evaluated
          </span>
        </AccordionHeader>
        <AccordionPanel>
          <p className="mb-2 mt-0 text-[12px]" style={{ color: TEXT.secondary }}>
            “Not flagged” is not “passed”: a device reports only the checks that blocked or
            warned, so this column also covers checks that did not run.
          </p>
          <DataTable
            columns={columns}
            data={rollup.rows}
            keyForRow={(r) => r.check}
            emptyState={
              <span className="text-[12px]" style={{ color: TEXT.secondary }}>
                No checks to report yet.
              </span>
            }
          />
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}
